import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { WhatsappMessageApiService } from '../services/whatsapp-message-api.service';
import {
  CreateTemplateRequest,
  WhatsappTemplate,
  WHATSAPP_TEMPLATE_CATEGORY_LABELS,
  WHATSAPP_TEMPLATE_STATUS_LABELS,
  whatsappLabel,
} from '../models/whatsapp.model';
import {
  WHATSAPP_TEMPLATE_SUGGESTIONS,
  WhatsappTemplateSuggestion,
} from '../models/whatsapp-template-suggestions';

/** Idiomas ofrecidos. Meta admite decenas; estos son los que usa el negocio. */
const LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'Inglés' },
];

@Component({
  selector: 'app-whatsapp-templates',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent],
  templateUrl: './whatsapp-templates.component.html',
})
export class WhatsappTemplatesComponent implements OnInit {
  private readonly api = inject(WhatsappMessageApiService);
  private readonly confirm = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);

  /** Para insertar el marcador donde está el cursor, no al final del texto. */
  @ViewChild('bodyInput') bodyInput?: ElementRef<HTMLTextAreaElement>;

  loading = true;
  saving = false;
  errorMessage: string | null = null;

  templates: WhatsappTemplate[] = [];

  formOpen = false;
  form: CreateTemplateRequest = this.emptyForm();
  /**
   * Lo que el usuario escribe como nombre. Meta sólo acepta minúsculas, números y guiones
   * bajos, así que se le pide un título normal y el nombre técnico se deriva de él: nadie
   * debería tener que aprenderse el formato de Meta para mandar un mensaje.
   */
  titleInput = '';

  readonly languages = LANGUAGES;
  readonly suggestions = WHATSAPP_TEMPLATE_SUGGESTIONS;
  suggestionsOpen = false;
  /** Nombre de la sugerencia que se está mandando a Meta, para bloquear su botón. */
  creatingSuggestion: string | null = null;
  /** La explicación de qué es una plantilla: se abre sola cuando no hay ninguna. */
  helpOpen = false;

  ngOnInit(): void {
    this.load();
  }

  // ═══ Listado ══════════════════════════════════════════════════════════════

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.api.listTemplates().subscribe({
      next: (list) => {
        this.templates = list;
        this.loading = false;
        // Sin ninguna aprobada no se puede escribir primero a nadie: se abren las sugerencias.
        if (this.approvedCount === 0) this.suggestionsOpen = true;
        if (this.templates.length === 0) this.helpOpen = true;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        // 404 = el negocio todavía no conectó WhatsApp; no es un error que haya que gritar.
        this.templates = [];
        if (err.status !== 404) {
          this.errorMessage = this.toErrorMessage(err, 'No se pudieron cargar las plantillas');
        }
      },
    });
  }

  /** Cuántas se pueden enviar hoy. Con 0 no se puede iniciar ninguna conversación. */
  get approvedCount(): number {
    return this.templates.filter((t) => t.status === 'APPROVED').length;
  }

  /** `recordatorio_de_cita` → «Recordatorio de cita». El nombre crudo es de Meta, no del usuario. */
  prettyName(name: string | null | undefined): string {
    if (!name) return '';
    const words = name.replace(/_/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  languageLabel(code: string | null | undefined): string {
    return LANGUAGES.find((l) => l.code === code)?.label ?? code ?? '';
  }

  categoryLabel(category: string | null | undefined): string {
    return whatsappLabel(WHATSAPP_TEMPLATE_CATEGORY_LABELS, category);
  }

  statusLabel(status: string | null | undefined): string {
    return whatsappLabel(WHATSAPP_TEMPLATE_STATUS_LABELS, status);
  }

  /** Qué significa el estado, en una frase. El nombre solo no dice si hay que hacer algo. */
  statusHint(status: string | null | undefined): string {
    switch (status) {
      case 'APPROVED':
        return 'Lista para enviar.';
      case 'PENDING':
        return 'Meta la está revisando. Suele tardar minutos.';
      case 'REJECTED':
        return 'Meta la rechazó. Corrige el texto y créala con otro nombre.';
      case 'PAUSED':
        return 'En pausa por reportes de los clientes. No se puede enviar.';
      case 'DISABLED':
        return 'Deshabilitada por Meta. No se puede enviar.';
      case 'IN_APPEAL':
        return 'En apelación ante Meta.';
      default:
        return '';
    }
  }

  /** Verde aprobada, ámbar en revisión, rojo rechazada; gris el resto. */
  statusClass(status: string | null | undefined): string {
    switch (status) {
      case 'APPROVED':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'PENDING':
      case 'IN_APPEAL':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'REJECTED':
      case 'DISABLED':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  }

  // ═══ Sugeridas ════════════════════════════════════════════════════════════

  /** Meta agrupa por nombre: si ya existe en cualquier estado, el alta choca. */
  isSuggestionCreated(s: WhatsappTemplateSuggestion): boolean {
    return this.templates.some((t) => t.name === s.template.name);
  }

  /** Carga la sugerencia en el formulario para editarla antes de mandarla. */
  useSuggestion(s: WhatsappTemplateSuggestion): void {
    this.form = {
      ...s.template,
      bodyExamples: [...s.template.bodyExamples],
      headerText: s.template.headerText ?? '',
      footerText: s.template.footerText ?? '',
    };
    this.titleInput = this.prettyName(s.template.name);
    this.formOpen = true;
    this.errorMessage = null;
  }

  /** Alta directa, sin pasar por el formulario. */
  createSuggestion(s: WhatsappTemplateSuggestion): void {
    if (this.creatingSuggestion) return;
    this.creatingSuggestion = s.template.name;
    this.errorMessage = null;

    this.api.createTemplate(s.template).subscribe({
      next: () => {
        this.creatingSuggestion = null;
        this.toast.created('Plantilla');
        // Nace en revisión: se recarga para que aparezca con ese estado y no parezca
        // que el alta no hizo nada.
        this.load();
      },
      error: (err) => {
        this.creatingSuggestion = null;
        this.errorMessage = this.toErrorMessage(err, 'No se pudo crear la plantilla');
      },
    });
  }

  // ═══ Formulario ═══════════════════════════════════════════════════════════

  toggleForm(): void {
    this.formOpen = !this.formOpen;
    if (this.formOpen) {
      this.form = this.emptyForm();
      this.titleInput = '';
    }
  }

  /** «Recordatorio de cita» → `recordatorio_de_cita`, que es lo único que Meta acepta. */
  onTitleChange(): void {
    this.form.name = (this.titleInput || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // acentos: Meta no los admite
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 512);
  }

  get nameValid(): boolean {
    return /^[a-z0-9_]{1,512}$/.test(this.form.name || '');
  }

  /** Meta agrupa por nombre: repetirlo es un error que sólo se ve al guardar. */
  get nameTaken(): boolean {
    return !!this.form.name && this.templates.some((t) => t.name === this.form.name);
  }

  setCategory(category: string): void {
    this.form.category = category;
  }

  // ── Los datos variables ───────────────────────────────────────────────────
  //
  // El usuario no escribe {{1}}: pulsa un botón y el marcador aparece donde tenía el
  // cursor. Escribirlo a mano era la parte que nadie que no sea programador adivina.

  get paramCount(): number {
    const matches = [...(this.form.bodyText || '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)];
    return matches.reduce((max, m) => Math.max(max, Number(m[1])), 0);
  }

  /** Los inputs de ejemplo se derivan del cuerpo: uno por cada {{n}}. */
  onBodyChange(): void {
    const count = this.paramCount;
    const current = this.form.bodyExamples;
    this.form.bodyExamples = Array.from({ length: count }, (_, i) => current[i] ?? '');
  }

  insertParam(): void {
    const marker = this.placeholder(this.paramCount + 1);
    const el = this.bodyInput?.nativeElement;
    const body = this.form.bodyText || '';

    if (!el) {
      this.form.bodyText = body + marker;
      this.onBodyChange();
      return;
    }

    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? start;
    this.form.bodyText = body.slice(0, start) + marker + body.slice(end);
    this.onBodyChange();
    // El caret queda tras el marcador para poder seguir escribiendo sin tocar el ratón.
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, start + marker.length);
    });
  }

  /** El marcador literal, armado aquí: escrito en la plantilla, Angular lo interpolaría. */
  placeholder(index: number): string {
    return `{{${index}}}`;
  }

  /** Marca de posición legible mientras no haya ejemplo, para que la vista previa no mienta. */
  private exampleFor(index: number): string {
    const value = (this.form.bodyExamples[index - 1] || '').trim();
    return value || `dato ${index}`;
  }

  /** Lo que verá el cliente: el cuerpo con los ejemplos ya sustituidos. */
  get bodyPreview(): string {
    return (this.form.bodyText || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, n) =>
      this.exampleFor(Number(n))
    );
  }

  get previewEmpty(): boolean {
    return !(this.form.bodyText || '').trim();
  }

  get canSubmit(): boolean {
    return (
      this.nameValid &&
      !this.nameTaken &&
      !!this.form.language.trim() &&
      !!this.form.category &&
      !!this.form.bodyText.trim() &&
      this.form.bodyExamples.every((e) => e.trim().length > 0) &&
      !this.saving
    );
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.saving = true;
    this.errorMessage = null;

    this.api
      .createTemplate({
        ...this.form,
        name: this.form.name.trim(),
        language: this.form.language.trim(),
        bodyText: this.form.bodyText.trim(),
        bodyExamples: this.form.bodyExamples.map((e) => e.trim()),
        headerText: this.form.headerText?.trim() || undefined,
        footerText: this.form.footerText?.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.formOpen = false;
          this.form = this.emptyForm();
          this.titleInput = '';
          this.toast.created('Plantilla');
          // Queda en revisión de Meta; se recarga para que se vea en la lista con ese estado.
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = this.toErrorMessage(err, 'No se pudo crear la plantilla');
        },
      });
  }

  async remove(t: WhatsappTemplate): Promise<void> {
    const ok = await this.confirm.confirmDanger({
      title: 'Borrar plantilla',
      message:
        `Se borrará "${this.prettyName(t.name)}" en Meta, incluidos todos sus idiomas. ` +
        'Ojo: Meta bloquea ese nombre unas semanas, así que no podrás volver a crearla igual. ¿Continuar?',
    });
    if (!ok) return;

    this.api.deleteTemplate(t.name).subscribe({
      next: () => {
        this.templates = this.templates.filter((x) => x.name !== t.name);
        this.toast.deleted('Plantilla');
      },
      error: (err) => {
        this.errorMessage = this.toErrorMessage(err, 'No se pudo borrar la plantilla');
      },
    });
  }

  private emptyForm(): CreateTemplateRequest {
    return {
      name: '',
      language: 'es',
      category: 'UTILITY',
      bodyText: '',
      bodyExamples: [],
      headerText: '',
      footerText: '',
    };
  }

  private toErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body: any = err.error;
      if (typeof body === 'string') return body;
      if (body?.message) return body.message;
      return `${err.status} ${err.statusText || fallback}`;
    }
    return fallback;
  }
}
